import {
  Checkmark16Regular,
  Checkmark20Regular,
  ClipboardPaste16Regular,
  ClipboardPaste20Regular,
} from '@fluentui/react-icons';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import './ClipboardButton.scss';

/** How long the confirmation checkmark stays visible, in milliseconds */
const CONFIRMATION_DURATION = 2000;

const COPY_ICONS = {
  medium: <ClipboardPaste20Regular />,
  small: <ClipboardPaste16Regular />,
};

const COPIED_ICONS = {
  medium: <Checkmark20Regular />,
  small: <Checkmark16Regular />,
};

export interface ClipboardButtonProps {
  /** Value written to the clipboard on click */
  value: string;
  /** Tooltip and accessible name, e.g. "Azure Id kopieren". Defaults to a generic text */
  label?: string;
  /** Renders the button with an outline instead of transparent */
  outline?: boolean;
  /**
   * `small` shrinks the button to 24px so it does not inflate the line height when it
   * sits next to a single line of text. Defaults to `medium` (32px).
   */
  size?: 'medium' | 'small';
  /** Additional class names for the button */
  className?: string;
}

/**
 * Icon button that copies a value to the clipboard and confirms with a checkmark.
 *
 * The tooltip doubles as the accessible name of the icon-only button, so `label` should
 * name the value, not the action alone.
 */
export const ClipboardButton: React.FC<ClipboardButtonProps> = ({
  value,
  label,
  outline,
  size = 'medium',
  className,
}) => {
  const { t } = useTranslation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  // The confirmation follows the write instead of preceding it: outside a secure context
  // `navigator.clipboard` is undefined, and even where it exists the write rejects when
  // permission is denied or the document is not focused. Confirming up front would show a
  // checkmark for a copy that never happened.
  const handleClick = () => {
    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard
      .writeText(value)
      .then(() => {
        setIsCopied(true);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setIsCopied(false);
        }, CONFIRMATION_DURATION);
      })
      .catch(() => {
        setIsCopied(false);
      });
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Tooltip
      content={label ?? t('copyToClipboard', { defaultValue: 'In die Zwischenablage kopieren' })}
      trigger={
        <Button
          className={cn(
            'clipboardButton',
            {
              'clipboardButton--copied': isCopied,
              'clipboardButton--small': size === 'small',
            },
            className,
          )}
          appearance={outline ? undefined : 'transparent'}
          size={size === 'small' ? 'small' : undefined}
          icon={isCopied ? COPIED_ICONS[size] : COPY_ICONS[size]}
          onClick={handleClick}
        />
      }
    />
  );
};
